import { objectType } from 'types';
import axios, { AxiosRequestConfig } from 'axios';
import { BaseXHR } from 'utils/axios';
import {
  getVariantMenu,
  getVariantOpeningHours,
  BOOKING_METHODS,
  BOOKING_SOURCE,
  RestaurantDetailResponse,
} from '../pages/Restaurants/config';
import { getVariantTimeIds } from '../pages/Restaurants/store';
import {
  getCuisineTypes,
  getFirstMediaUrl,
  getMediaUrls,
} from '../pages/Restaurants/reservationCmsUtils';

export type ReservationBookingItemPayload = {
  itemIndex?: string;
  storeId?: string;
  variantId?: string;
  zoneId?: string;
  reservationDate: string;
  reservationTime: string;
  bookingMethod?: BOOKING_METHODS;
  bookingSource?: BOOKING_SOURCE;
  quantity: number;
  freeChildCount?: number;
};

export type CreateReservationBookingPayload = {
  currency: string;
  reservation: {
    fullname?: string;
    Firstname?: string;
    Lastname?: string;
    phoneNumber: string;
    email: string;
    note?: string;
    paymentMethod?: string;
    items: ReservationBookingItemPayload[];
    isFree?: number
  };
};

export type UpdateReservationBookingPayload = {
  quantity: number;
  infantQuantity?: number;
  representativeName: string;
  representativePhone: string;
  arrivalDate: string;
  storeId: string;
  ticketShowId: string;
  zoneId: number | string;
  referentCode?: string;
  reservationCode: string;
};

export type CancelReservationBookingPayload = {
  orderId: string;
};

export type ApproveReservationPaymentPayload = {
  orderId: string;
  approveStatus: 'Approved' | 'PaymentRejected';
};

export type ReservationBookingDetailParams = {
  reservationNo: string;
};

export type ReservationBookingHistoriesParams = objectType;

export type ReservationBookingProductsParams = objectType;

export type ReservationBookingAvailabilityParams = {
  storeId?: string;
  zoneId?: string;
  partySize: number;
  reservationDate: string;
  reservationTime?: string;
  variantId?: string;
  bookingMethod: 'DEPOSIT_RESERVATION' | 'TICKET_RESERVATION';
};

export type ReservationCmsItemListParams = objectType;

export type ReservationSlotStatus = 'available' | 'deposit' | 'full';

export type ReservationBookingSlot = {
  time?: string;
  timeSlotLabel: string;
  timeSlotValue: string;
  label?: string;
  status: ReservationSlotStatus;
  depositAmount?: number;
};

export type ReservationVerifyParams = {
  restaurantId: string;
  bookingDate: string;
  adult: number;
  child: number;
  reservationProduct?: any;
};

export type ReservationVerifyResponse = {
  code: number;
  data: {
    slots: ReservationBookingSlot[];
  };
};

export const createReservationBookingApi = (
  data: CreateReservationBookingPayload,
  config?: AxiosRequestConfig,
) => BaseXHR.$post('b2b/reservation/v2/booking/create', data, config);

export const updateReservationBookingApi = (
  data: UpdateReservationBookingPayload,
) => BaseXHR.$post('b2b/reservation/v2/booking/update', data);

export const cancelReservationBookingApi = (
  data: CancelReservationBookingPayload,
) => BaseXHR.$post('b2b/reservation/v2/booking/cancel', data);

export const paymentReservationBookingApi = (
  data: UpdateReservationBookingPayload,
  config?: AxiosRequestConfig,
) => BaseXHR.$post('b2b/reservation/v2/booking/payment', data, config);

export const approveReservationPaymentApi = (
  data: ApproveReservationPaymentPayload,
  config?: AxiosRequestConfig,
) => BaseXHR.$post('b2b/reservation/v2/booking/approve-payment', data, config);

export const addTicketToReservationApi = (file: File, orderId: string) => {
  const formData = new FormData();
  formData.append('ticketFile', file);
  formData.append('orderId', orderId);
  return BaseXHR.$post(
    'b2b/reservation/ticket/add-ticket-to-reservation',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      skipErrorNotify: true,
    },
  );
};

const getResponsePayload = (response: any) =>
  response?.data?.data ?? response?.data;

const getResponseItems = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.list)) return payload.list;

  return [];
};

const getRestaurantImageUrl = (item: any) =>
  [
    getFirstMediaUrl(item),
    item?.image,
    item?.imageUrl,
    item?.thumbnail,
    item?.thumbnailUrl,
    item?.avatar,
    item?.banner,
  ].find(Boolean) ?? '';

export const getReservationBookingDetailApi = async ({
  reservationNo,
}: ReservationBookingDetailParams) => {
  const response = await BaseXHR.$get('b2b/reservation/v2/booking/histories', {
    reservationNo,
  });
  const payload = getResponsePayload(response);
  const items = getResponseItems(payload);

  return {
    ...response,
    data: items[0],
  };
};

export const getReservationBookingTicketDetailApi = ({
  reservationNo,
}: ReservationBookingDetailParams) =>
  BaseXHR.$get(
    `b2b/reservation/v2/booking/detail/${encodeURIComponent(reservationNo)}`,
  );

export const getReservationBookingProductsApi = (
  params?: ReservationBookingProductsParams,
) => BaseXHR.$get('b2b/reservation/v2/booking/products', params);

export const getReservationBookingStoresApi = (
  params?: ReservationBookingProductsParams,
) => BaseXHR.$get('b2b/reservation/store/listing', params);

export const getReservationBookingAvailabilityApi = (
  params:
    | ReservationBookingAvailabilityParams
    | ReservationBookingAvailabilityParams[],
  config?: AxiosRequestConfig,
) => BaseXHR.$post('b2b/reservation/v2/booking/availability', params, config);

export const getReservationCmsItemListApi = (
  params?: ReservationCmsItemListParams,
  config?: AxiosRequestConfig,
) =>
  axios.get(
    `${process.env.REACT_APP_CMS_RESERVATION_ENV}/public-api/api/public/v1/data-objects/reservation-nha-hang/${params?.id}`,
    {
      ...config,
      headers: {
        'x-api-key': process.env.REACT_APP_CMS_RESERVATION_API_KEY ?? '',
        'x-site-domain':
          process.env.REACT_APP_CMS_RESERVATION_SITE_DOMAIN ?? '',
        ...config?.headers,
      },
    },
  );

export const getReservationBookingHistoriesApi = (
  params?: ReservationBookingHistoriesParams,
) => BaseXHR.$get('b2b/reservation/v2/booking/histories', params);

export const getReservationBookingApproveHistoriesApi = (
  params?: ReservationBookingHistoriesParams,
) => BaseXHR.$get('b2b/reservation/v2/booking/approve-histories', params);

const getTimeMinutes = (time?: string) => {
  if (!time) return undefined;

  const [hour, minute] = time.split(':').map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return undefined;

  return hour * 60 + minute;
};

const isPeriodBookable = (period: any) =>
  period?.isAvailable !== false && Number(period?.bookable ?? 0) > 0;

const isTimeInPeriod = (time: string, period: any) => {
  const timeMinutes = getTimeMinutes(time);
  const startMinutes = getTimeMinutes(period?.startTime ?? period?.start_time);
  const endMinutes = getTimeMinutes(period?.endTime ?? period?.end_time);

  if (
    timeMinutes === undefined ||
    startMinutes === undefined ||
    endMinutes === undefined
  ) {
    return false;
  }

  return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
};

const getVariantAvailabilityPeriods = (
  variant: any,
  reservationProduct?: any,
) => {
  const storeId = String(variant?.storeId ?? '');
  const variantAvailability = Array.isArray(variant?.availability)
    ? variant.availability
    : variant?.availability
    ? [variant.availability]
    : [];
  const productAvailability = (
    Array.isArray(reservationProduct?.availability)
      ? reservationProduct.availability
      : []
  ).filter(
    (item: any) =>
      !storeId ||
      String(item?.storeId ?? item?.data?.storeId ?? '') === storeId,
  );
  const records = variantAvailability.length
    ? variantAvailability
    : productAvailability;

  return records.flatMap((item: any) => {
    const availability = item?.data?.data ?? item?.data ?? item;
    return Array.isArray(availability?.periods) ? availability.periods : [];
  });
};

const getVariantListingTimeSlots = (variant: any) => {
  if (Array.isArray(variant?.time_slots) && variant.time_slots.length) {
    return variant.time_slots;
  }
  if (Array.isArray(variant?.timeSlots) && variant.timeSlots.length) {
    return variant.timeSlots;
  }

  return (Array.isArray(variant?.periods) ? variant.periods : []).flatMap(
    (period: any) =>
      Array.isArray(period?.timeSlots)
        ? period.timeSlots
        : Array.isArray(period?.time_slots)
        ? period.time_slots
        : [],
  );
};

const buildPeriodSlots = (periods: any[]): ReservationBookingSlot[] =>
  periods.map((period: any) => ({
    timeSlotValue: period?.timeSlotValue,
    timeSlotLabel: period?.timeSlotLabel,
    status: isPeriodBookable(period) ? 'available' : 'full',
  }));

export const verifyReservationSlots = async ({
  restaurantId,
  reservationProduct,
}: ReservationVerifyParams): Promise<ReservationVerifyResponse> => {
  const variants = Array.isArray(reservationProduct?.variants)
    ? reservationProduct.variants
    : [];
  const variant = variants.find((item: any) =>
    [item?.storeId, item?.id, item?.variantId]
      .filter(value => value !== undefined && value !== null)
      .some(value => `${value}` === restaurantId),
  );
  const timeSlots = getVariantListingTimeSlots(variant);
  const periods = getVariantAvailabilityPeriods(variant, reservationProduct);
  const hasMappedTimeSlots = Array.isArray(variant?.timeSlotInformation);
  const mappedTimeSlots = hasMappedTimeSlots
    ? variant.timeSlotInformation.map((slot: any) => ({
        time: slot?.timeSlotValue,
        label: slot?.timeLabel,
        status: isPeriodBookable(slot) ? 'available' : 'full',
      }))
    : undefined;
  const slotsToDisplay = hasMappedTimeSlots
    ? mappedTimeSlots
    : timeSlots.length
    ? timeSlots
    : buildPeriodSlots(periods);

  return {
    code: variant ? 1 : 0,
    data: {
      slots: slotsToDisplay
        .map((slot: any): ReservationBookingSlot => {
          const time =
            slot?.value ?? slot?.time ?? slot?.startTime ?? slot?.label;
          if (slot?.status) {
            return {
              time,
              timeSlotValue: slot?.timeSlotValue,
              timeSlotLabel:
                slot?.label ?? slot?.timeSlotLabel ?? slot?.timeSlotValue,
              status: slot.status,
            };
          }
          const matchedPeriod = periods.find((period: any) =>
            isTimeInPeriod(time, period),
          );
          const hasPeriodAvailability = periods.length > 0;

          return {
            time,
            timeSlotValue: slot?.timeSlotValue,
            timeSlotLabel: slot?.label ?? slot?.periodName ?? slot?.period_name,
            status:
              !hasPeriodAvailability ||
              (matchedPeriod !== undefined && isPeriodBookable(matchedPeriod))
                ? 'available'
                : 'full',
          };
        })
        .filter(slot => slot.time),
    },
  };
};

export type ReservationHistoryDetailStatus =
  | 'Processing'
  | 'OnHold'
  | 'PendingApprove'
  | 'Confirmed'
  | 'Seated'
  | 'Completed'
  | 'Cancelled'
  | 'Rejected'
  | 'NoShow'
  | 'Expired'
  | 'pending'
  | 'failed'
  | 'cancelled'
  | 'success'
  | 'success-deposit';

export type DetailTicket = {
  ticketCode: string;
  assignmentStatus: string;
  usageStatus: string;
  ticketStatus: string;
  assignedAt: string;
  isLongTerm: boolean;
  usageDateFrom: string;
  usageDateTo: string;
};
export type ReservationHistoryDetail = {
  code: string;
  status: ReservationHistoryDetailStatus;
  restaurantName: string;
  bookingCode: string;
  bookingTime: string;
  guestAmount: string;
  specialRequest: string;
  depositAmount?: string;
  qrImage: string;
  logo: string;
  paymentStatus?: string;
  approveStatus?: string;
  price?: number;
  currency?: {
    value?: string;
    exChangeRate?: any;
  };
  cancelInformation?: {
    cancelledAt?: string;
    cancelledBy?: string | number;
    cancellationReason?: string;
  };
  externalReason?: string;
  bookingSource?: BOOKING_SOURCE; //trả về trong API detail
  siteName?: string; //trả về trong API detail
  storeName?: string; //trả về trong API detail
  zoneName?: string; //trả về trong API detail
  reservationNo?: string; //trả về trong API detail
  reservationDate?: string; //trả về trong API detail
  reservationTime?: string; //trả về trong API detail
  tickets?: DetailTicket[]; //trả về trong API detail
  bookingMethod?: BOOKING_METHODS; //trả về trong API detail
};

export type ReservationHistoryDetailParams = {
  code: string;
  status?: ReservationHistoryDetailStatus;
};

export type ReservationHistoryDetailResponse = {
  code: number;
  data: ReservationHistoryDetail;
};

export const getRestaurantDetail = async (
  slug: string,
  reservationProduct?: any,
): Promise<RestaurantDetailResponse> => {
  const variants = Array.isArray(reservationProduct?.variants)
    ? reservationProduct.variants
    : [];
  const variant = variants.find(
    (item: any) =>
      `${item?.storeId ?? item?.id ?? item?.variantId ?? ''}` === slug,
  );

  return {
    code: variant ? 1 : 0,
    data: variant
      ? {
          id: `${variant?.storeId ?? variant?.id ?? variant?.variantId ?? ''}`,
          destinationId:
            variant?.siteId ?? variant?.site_id ?? variant?.site?.id ?? '',
          timeIds: getVariantTimeIds(variant),
          priceRangeId: variant?.priceRangeId ?? '',
          cuisineIds: variant?.cuisineIds ?? getCuisineTypes(variant),
          titleKey:
            variant?.storeName ??
            variant?.store_name ??
            variant?.titleFallback ??
            variant?.name ??
            variant?.productName ??
            '',
          priceRangeKey: variant?.priceRangeKey ?? variant?.priceRange ?? '',
          locationKey: variant?.address ?? '',
          image: getRestaurantImageUrl(variant),
          tagKeys: variant?.tagKeys ?? getCuisineTypes(variant),
          statusKey: variant?.statusKey ?? '',
          bookingMethods: variant?.bookingMethods ?? [],
          booking_rules: variant?.bookingRules ?? variant?.booking_rules,
          availability: variant?.availability ?? [],
          description: variant?.description ?? variant?.shortDescription ?? '',
          openingHours: getVariantOpeningHours(variant),
          openingPeriods: Array.isArray(variant?.periods)
            ? variant.periods
            : [],
          timeSlots: Array.isArray(variant?.time_slots)
            ? variant.time_slots
            : [],
          media: getMediaUrls(variant),
          menu: getVariantMenu(variant, ''),
        }
      : undefined,
  };
};
